import React from "react";
import { styled } from "@mui/material/styles";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Input from "../../../component-lib/Atoms/Input/Input";
import Button from "../../../component-lib/Molecules/button/Button";
import EventEHRSwitchButton from "../../../component-lib/Molecules/switch-button/EventEHRSwitchButton";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: "0px 24px 16px 24px",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    borderRadius: "8px",
    width: "600px",
    maxWidth: "none",
  },
}));

const NAME_FIELD = "name";
const DESCRIPTION_FIELD = "description";
const STATUS_FIELD = "status";

interface MasterDataDialogProps {
  isOpen: boolean;
  title: string;
  nameLabel: string;
  handleClose: () => void;
  handleData: (data: {
    name: string;
    description: string;
    status: "Active" | "InActive";
  }) => void;
  defaultData?: any;
}

export default function MasterDataDialog({
  isOpen,
  title,
  nameLabel,
  handleClose,
  handleData,
  defaultData,
}: MasterDataDialogProps) {
  const validationSchema = Yup.object().shape({
    [NAME_FIELD]: Yup.string()
      .trim()
      .max(100, `${nameLabel} should not exceed 100 characters`)
      .required(`${nameLabel} is required`),
  });

  return (
    <BootstrapDialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="master-data-title"
      open={isOpen}
    >
      <Formik
        initialValues={{
          [NAME_FIELD]: defaultData?.name || "",
          [DESCRIPTION_FIELD]: defaultData?.description || "",
          [STATUS_FIELD]: (defaultData?.status === "InActive"
            ? "InActive"
            : "Active") as "Active" | "InActive",
        }}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          try {
            handleData({
              name: (values[NAME_FIELD] as string).trim(),
              description: (values[DESCRIPTION_FIELD] as string).trim(),
              status: values[STATUS_FIELD] as "Active" | "InActive",
            });
            resetForm();
          } catch (error) {
            console.log(error);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          values,
          touched,
          setFieldValue,
          handleBlur,
          handleSubmit,
        }) => {
          const isActive = values[STATUS_FIELD] === "Active";
          return (
            <Form onSubmit={handleSubmit}>
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  fontFamily: "inter",
                }}
                id="master-data-title"
              >
                {title}
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <hr />
              <DialogContent>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: "16px",
                  }}
                >
                  <div className="pt-4">
                    <Input
                      textFieldLabel={nameLabel}
                      size="small"
                      name={NAME_FIELD}
                      value={values[NAME_FIELD] as string}
                      maxLength={100}
                      onChangeCb={(e) => {
                        setFieldValue(NAME_FIELD, e.target.value);
                      }}
                      onBlur={handleBlur}
                      error={
                        touched[NAME_FIELD] && Boolean(errors[NAME_FIELD])
                      }
                      helperText={
                        touched[NAME_FIELD]
                          ? (errors[NAME_FIELD] as string | undefined)
                          : undefined
                      }
                    />
                  </div>
                  <div>
                    <Input
                      textFieldLabel="Description"
                      multiline={true}
                      maxWords={250}
                      rows={4}
                      size="small"
                      name={DESCRIPTION_FIELD}
                      value={values[DESCRIPTION_FIELD] as string}
                      onChangeCb={(e) => {
                        setFieldValue(DESCRIPTION_FIELD, e.target.value);
                      }}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#393939",
                        fontWeight: 500,
                        marginRight: "8px",
                      }}
                    >
                      Status:
                    </span>
                    <EventEHRSwitchButton
                      checked={isActive}
                      onChange={(_e, checked) => {
                        setFieldValue(
                          STATUS_FIELD,
                          checked ? "Active" : "InActive"
                        );
                      }}
                    />
                  </div>
                </div>
              </DialogContent>
              <DialogActions style={{ padding: "24px" }}>
                <Button
                  label={TEXT_CONSTANTS?.CANCEL}
                  variant="outlined"
                  onClickCb={handleClose}
                  style={{
                    width: "120px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                    backgroundColor: "white",
                    border: "none",
                    color: "#393939",
                  }}
                />
                <Button
                  type="submit"
                  label={TEXT_CONSTANTS?.SAVE}
                  variant="contained"
                  onClickCb={() => {}}
                  style={{
                    width: "200px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                  }}
                />
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </BootstrapDialog>
  );
}
